/**
 * Pure geometry for the demo storyboard — spotlight rect resolution + card
 * placement math. Kept dependency-free (only the DOM/`window`) so it's unit
 * testable in isolation, away from the React/Framer renderer.
 */
import type { TourPlacement } from "./demoTourSteps";

export const CARD_WIDTH = 360;
export const GAP = 14; // px between the spotlight and the card
export const MARGIN = 12; // viewport clamp margin
export const SPOT_PAD = 8; // spotlight padding around the target

export interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

/** Read a target's viewport rect by selector, or ``null`` if absent/collapsed. */
export function readRect(selector: string): Rect | null {
  const el = document.querySelector(selector);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  if (r.width === 0 && r.height === 0) return null;
  return { top: r.top, left: r.left, width: r.width, height: r.height };
}

/** Place the card relative to the spotlight, clamped to the viewport. */
export function placeCard(
  rect: Rect | null,
  placement: TourPlacement,
  cardH: number,
): { top: number; left: number } {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  if (!rect || placement === "center") {
    return { top: Math.max(MARGIN, (vh - cardH) / 2), left: (vw - CARD_WIDTH) / 2 };
  }
  const cx = rect.left + rect.width / 2;
  let top: number;
  let left: number;
  switch (placement) {
    case "top":
      top = rect.top - cardH - GAP;
      left = cx - CARD_WIDTH / 2;
      break;
    case "left":
      top = rect.top + rect.height / 2 - cardH / 2;
      left = rect.left - CARD_WIDTH - GAP;
      break;
    case "right":
      top = rect.top + rect.height / 2 - cardH / 2;
      left = rect.left + rect.width + GAP;
      break;
    default: // bottom
      top = rect.top + rect.height + GAP;
      left = cx - CARD_WIDTH / 2;
      break;
  }
  left = Math.min(Math.max(MARGIN, left), vw - CARD_WIDTH - MARGIN);
  top = Math.min(Math.max(MARGIN, top), vh - cardH - MARGIN);
  return { top, left };
}
